/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, ThemeProviderProps } from './ThemeContext';
import { createMuiTheme } from './muiTheme';
import { useTheme } from './hooks';

/**
 * Inner component that reads the current theme mode from context
 * and creates a mode-aware MUI theme. This ensures MUI palette
 * colors update when the user toggles light/dark.
 */
const MuiThemeSync: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { mode } = useTheme();
    
    // Re-create MUI theme when mode changes.
    // Because ThemeContext eagerly toggles the .dark class on <html> inside its state setters,
    // getCSSVar will read the already-updated CSS variables during this render.
    const muiTheme = useMemo(() => createMuiTheme(), [mode]);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <CssBaseline />
            {children}
        </MuiThemeProvider>
    );
};

export const AppThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    initialTheme,
}) => {
    return (
        <ThemeProvider initialTheme={initialTheme}>
            <MuiThemeSync>
                {children}
            </MuiThemeSync>
        </ThemeProvider>
    );
};

export default AppThemeProvider;
